import { valueProvider, noop, isPresent, arrayOfElements, asis, valuesProvider, isDefined, isArrayOfElements } from '@proc7ts/primitives';
import { NamespaceDef, newNamespaceAliaser, isQualifiedName, css__naming } from '@frontmeans/namespace-aliaser';
import { newRenderSchedule } from '@frontmeans/render-scheduler';
import { AfterEvent__symbol, trackValue, mapAfter_, ValueTracker, EventEmitter, OnEvent__symbol, mapAfter, digAfter, afterAll, translateAfter, translateOn, afterSupplied, isEventKeeper, supplyAfter, afterThe, afterEach, afterSent, mapOn, digAfter_, onceAfter, afterEventBy, shareAfter, EventNotifier } from '@proc7ts/fun-events';
import { Supply, neverSupply } from '@proc7ts/supply';
import { overElementsOf, filterArray, itsReduction, itsElements, itsEach, overEntries, mapIt, overIterator, PushIterator__symbol, iteratorOf, mapArray, flatMapIt, overNone, overArray, flatMapArray, filterIt, itsEvery, valueArray, reverseArray } from '@proc7ts/push-iterator';
import { DomEventDispatcher } from '@frontmeans/dom-events';
import { DeltaSet } from '@proc7ts/delta-set';

const nullInAspect$ = {
    instance: null,
    convertTo: noop,
};
/**
 * Creates an aspect applied to control with `null` instance value.
 *
 * @category Aspect
 * @typeParam TValue - Input value type.
 * @typeParam TInstance - Aspect instance type.
 *
 * @returns Applied input aspect.
 */
function nullInAspect() {
    return nullInAspect$;
}
/**
 * Creates an aspect applied to control with known instance.
 *
 * An instance remains as is when converted to another control.
 *
 * @category Aspect
 * @typeParam TValue - Input value type.
 * @typeParam TInstance - Aspect instance type.
 * @typeParam instance - Known aspect instance type.
 *
 * @returns Applied input aspect.
 */
function knownInAspect(instance) {
    return {
        instance,
        convertTo(_target) {
            return this;
        },
    };
}
/**
 * Creates an aspect applied to control with known inconvertible instance.
 *
 * When converted to another control with the same value, an aspect instance remains as is.
 *
 * When converted to another control with another value, an aspect instance replaced by `null`.
 *
 * @category Aspect
 * @typeParam TValue - Input value type.
 * @typeParam TInstance - Aspect instance type.
 * @typeParam TKind - Aspect application kind.
 * @param control - Target control to apply an aspect to.
 * @param aspectKey - Applied aspect key.
 * @param instance - Known aspect instance type. Defaults to `null`.
 *
 * @returns Applied input aspect.
 */
function inconvertibleInAspect(control, aspectKey, instance = null) {
    return builtInAspect$(control, aspectKey, valueProvider(null), instance);
}
/**
 * Creates an aspect applied to control with built instance.
 *
 * When converted to another control with the same value, an aspect instance remains as is.
 *
 * When converted to another control with another value, an aspect instance is built again with original control
 * passed to the builder as a second parameter.
 *
 * @category Aspect
 * @typeParam TValue - Input value type.
 * @typeParam TInstance - Aspect instance type.
 * @typeParam TKind - Aspect application kind.
 * @param control - Target control to apply an aspect to.
 * @param aspectKey - Applied aspect key.
 * @param build - Aspect instance builder function. Accepts target control and optionally an original one as parameters.
 *
 * @returns Applied input aspect.
 */
function builtInAspect(control, aspectKey, build) {
    return builtInAspect$(control, aspectKey, build);
}
function builtInAspect$(control, aspectKey, build, instance, origin) {
    if (instance === undefined) {
        instance = build(control, origin);
    }
    return {
        instance,
        convertTo(target) {
            return builtInAspect$(target, aspectKey, build, undefined, control);
        },
        attachTo(target) {
            return builtInAspect$(target, aspectKey, build, control.aspect(aspectKey) || build(target));
        },
    };
}

/**
 * A symbol of aspect key property containing a reference to aspect.
 *
 * @category Aspect
 */
const InAspect__symbol = ( /*#__PURE__*/Symbol('in-aspect'));

/**
 * Default input-aspects namespace definition.
 *
 * @category Aspect
 */
const InputAspects__NS = ( /*#__PURE__*/new NamespaceDef('https://surol.github.io/input-aspects/ns', 'inasp', 'input-aspects'));
/**
 * @internal
 */
const InNamespaceAliaser__aspect = {
    applyTo() {
        return knownInAspect(newNamespaceAliaser());
    },
};
/**
 * @category Aspect
 */
const InNamespaceAliaser = {
    get [InAspect__symbol]() {
        return InNamespaceAliaser__aspect;
    },
    /**
     * Creates input control aspect converter that assigns the given namespace aliaser to converted control.
     *
     * @typeParam TValue - Converted control value type.
     * @param nsAlias - Target namespace aliaser.
     *
     * @returns Input control aspect converter.
     */
    to(nsAlias) {
        return {
            applyAspect(aspect) {
                return aspect === InNamespaceAliaser__aspect
                    ? knownInAspect(nsAlias)
                    : undefined;
            },
        };
    },
};

/**
 * @internal
 */
const InRenderScheduler__aspect = {
    applyTo() {
        return knownInAspect(newRenderSchedule);
    },
};
/**
 * @category Aspect
 */
const InRenderScheduler = {
    get [InAspect__symbol]() {
        return InRenderScheduler__aspect;
    },
    /**
     * Creates input control aspect converter that assigns the given render scheduler to converted control.
     *
     * @typeParam TValue - Converted control value type.
     * @param scheduler - Target DOM render scheduler.
     *
     * @returns Input control aspect converter.
     */
    to(scheduler) {
        return {
            applyAspect(aspect) {
                return aspect === InRenderScheduler__aspect
                    ? knownInAspect(scheduler)
                    : undefined;
            },
        };
    },
};

const InRole__aspect = {
    applyTo(control) {
        return {
            instance: new InControlRole(control),
            convertTo: noop,
        };
    },
};
/**
 * A role of input control.
 *
 * Contains arbitrary named roles. A special role `'default'` considered active when no other roles activated.
 *
 * Roles may be used to conditionally activate other input aspects of the control.
 *
 * @typeParam TValue - Input value type.
 */
class InRole {
    static get [InAspect__symbol]() {
        return InRole__aspect;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}
class InRole$Active {
    constructor(roles) {
        this.roles = roles;
        this._activate = noop;
        this.active = {
            [Symbol.iterator]() {
                return roles.keys();
            },
            has(role) {
                return roles.has(role);
            },
        };
    }
    static create() {
        const result = new InRole$Active(new Map());
        result.add('default', true);
        return result;
    }
    modify() {
        const result = new InRole$Active(this.roles);
        result._defaultSupply = this._defaultSupply;
        return result;
    }
    add(role, isDefault) {
        const named = this.roles.get(role);
        if (named) {
            // Already active.
            ++named.active;
            return;
        }
        const supply = new Supply();
        this.roles.set(role, {
            active: 1,
            supply,
        });
        if (isDefault) {
            this._defaultSupply = supply.whenOff(() => {
                const toRemove = this.roles.get(role);
                if (!--toRemove.active) {
                    this.roles.delete(role);
                }
            });
        }
        else {
            const defaultSupply = this._defaultSupply;
            this.activateBy(() => defaultSupply.off());
        }
        return supply;
    }
    remove(role, reason) {
        const named = this.roles.get(role);
        if (--named.active) {
            // Still active.
            return;
        }
        this.roles.delete(role);
        this.activateBy(() => named.supply.off(reason));
    }
    activateBy(activator) {
        const prevActivator = this._activate;
        this._activate = () => {
            prevActivator();
            activator();
        };
    }
    activate() {
        const activator = this._activate;
        this._activate = noop;
        activator();
    }
}
class InControlRole extends InRole {
    constructor(_control) {
        super();
        this._control = _control;
        this._active = trackValue(InRole$Active.create());
        this._activators = new Map();
        this._active.on(active => active.activate());
        this._active.supply.needs(_control);
        this.read = this._active.read.do(mapAfter_(({ active }) => active));
    }
    add(role) {
        const active = this._active.it.modify();
        this._add(active, role);
        this._active.it = active;
        return new Supply(reason => {
            const active = this._active.it.modify();
            active.remove(role, reason);
            if (!active.roles.size) {
                // No more active roles left.
                // Enable `default` role.
                this._add(active, 'default', true);
            }
            this._active.it = active;
        });
    }
    when(role, activator) {
        let activators = this._activators.get(role);
        if (!activators) {
            activators = new Map();
            this._activators.set(role, activators);
        }
        const supply = new Supply().needs(this._control);
        activators.set(supply, (control, role, active) => activator(control, role, active).supply.needs(supply));
        supply.whenOff(() => {
            activators.delete(supply);
            if (!activators.size) {
                this._activators.delete(role);
            }
        });
        const named = this._active.it.roles.get(role);
        if (named) {
            // The role already active.
            // Issue activator immediately.
            named.supply.cuts(activator(this._control, role, this._active.it.active));
        }
        return supply;
    }
    _add(active, role, isDefault = false) {
        const activatedSupply = active.add(role, isDefault);
        if (activatedSupply) {
            // Role activated
            const activators = this._activators.get(role);
            if (activators) {
                // Issue activators
                for (const activator of activators.values()) {
                    activatedSupply.cuts(activator(this._control, role, this._active.it.active));
                }
            }
        }
    }
}

/**
 * @internal
 */
function isAspectKey(value) {
    return InAspect__symbol in value;
}

/**
 * @internal
 */
const noopInConversion = {
    applyAspect(_aspect) {
        return;
    },
};
/**
 * @internal
 */
function noopInConverter() {
    return noopInConversion;
}

function intoConvertedBy(valueOrAspectConverter, ...converters) {
    if (!valueOrAspectConverter) {
        return noopInConverter;
    }
    const converter = inConverter(valueOrAspectConverter);
    if (!converters.length) {
        return converter;
    }
    const aspectConverters = converters.map(inConverter);
    return (from, to) => {
        const conversion = converter(from, to);
        const conversions = overElementsOf([conversion], filterArray(aspectConverters.map(acf => acf(from, to)), isPresent));
        const applyAspect = itsReduction(conversions, (prev, cv) => cv.applyAspect
            ? (aspect => prev(aspect) || cv.applyAspect(aspect))
            : prev, noop);
        if ( /*#__INLINE__*/isInAspectConversion(conversion)) {
            return {
                applyAspect,
            };
        }
        return {
            set: conversion.set.bind(conversion),
            get: conversion.get.bind(conversion),
            applyAspect,
        };
    };
}
/**
 * Creates converter that combines input aspect converters.
 *
 * @category Converter
 * @typeParam TValue - Input value type.
 * @param aspects - Input aspect converter(s) to combine.
 *
 * @returns Input aspect conversion factory.
 */
function intoConvertedAspects(aspects) {
    return aspects
        ? intoConvertedBy(...arrayOfElements(aspects))
        : intoConvertedBy();
}
/**
 * Checks whether the given input control converter converts aspect only.
 *
 * @category Converter
 * @param conversion - Input control conversion to check.
 *
 * @returns `false` if the given conversion has a {@link InConverter.Value.Conversion.set set} method,
 * or `true` if there is no one.
 */
function isInAspectConversion(conversion) {
    return !conversion.set;
}
function inConverter(converter) {
    return typeof converter === 'function' ? converter : valueProvider(converter);
}

/**
 * @internal
 */
const InBuilder$Impl__symbol = ( /*#__PURE__*/Symbol('InBuilder.impl'));
/**
 * @internal
 */
class InBuilder$Impl {
    constructor() {
        this._aspectsByKey = new Map();
        this._commonAspects = undefined;
        this._setup = undefined;
    }
    addAspect(aspect, converter) {
        const prev = this._aspectsByKey.get(aspect);
        this._aspectsByKey.set(aspect, prev ? intoConvertedBy(prev, converter) : intoConvertedBy(converter));
    }
    addAspects(aspects) {
        this._commonAspects = this._commonAspects
            ? intoConvertedBy(this._commonAspects, ...aspects)
            : intoConvertedBy(...aspects);
    }
    setup(setup) {
        const prev = this._setup;
        if (prev) {
            this._setup = control => {
                prev(control);
                setup(control);
            };
        }
        else {
            this._setup = setup;
        }
    }
    build(factory) {
        const control = factory({ aspects: this._aspects() });
        // Control setup
        if (this._setup) {
            control.setup(this._setup);
        }
        return control;
    }
    _aspects() {
        if (this._aspectsByKey.size) {
            const byKey = intoConvertedByKey(this._aspectsByKey);
            return this._commonAspects ? intoConvertedBy(byKey, this._commonAspects) : byKey;
        }
        return this._commonAspects;
    }
}
function intoConvertedByKey(byKey) {
    return (from, to) => ({
        applyAspect(aspect) {
            const converter = byKey.get(aspect);
            return converter && converter(from, to).applyAspect(aspect);
        },
    });
}

var _a;
/**
 * User input control builder.
 *
 * @category Control
 * @typeParam TControl - Control type.
 * @typeParam TValue - Input value type.
 */
class InBuilder {
    constructor() {
        /**
         * @internal
         */
        this[_a] = new InBuilder$Impl();
    }
    /**
     * Registers an aspect to add to the built control.
     *
     * @param aspectKey - A key of aspect to add.
     * @param converter - An aspect converter to the built control from the {@link inValueOf same-valued one}.
     *
     * @returns `this` builder instance.
     */
    addAspect(aspectKey, converter) {
        this[InBuilder$Impl__symbol].addAspect(aspectKey[InAspect__symbol], converter);
        return this;
    }
    /**
     * Registers arbitrary aspects to add to the built control.
     *
     * These aspects always applied after {@link addAspect concrete} ones.
     *
     * @param aspects - Input aspects to add. These are aspect converters to the built control from the {@link inValueOf
     * same-valued one}.
     *
     * @returns `this` builder instance.
     */
    addAspects(...aspects) {
        if (aspects.length) {
            this[InBuilder$Impl__symbol].addAspects(aspects);
        }
        return this;
    }
    setup(aspectKeyOrSetup, aspectSetup) {
        if (isAspectKey(aspectKeyOrSetup)) {
            this[InBuilder$Impl__symbol].setup(control => control.setup(aspectKeyOrSetup, aspectSetup));
        }
        else {
            this[InBuilder$Impl__symbol].setup(aspectKeyOrSetup);
        }
        return this;
    }
    /**
     * Builds control.
     *
     * @param factory - Control factory.
     *
     * @returns New user input control constructed by the given `factory` and set up with configured aspects and setup
     * procedures.
     */
    build(factory) {
        return this[InBuilder$Impl__symbol].build(factory);
    }
}
_a = InBuilder$Impl__symbol;

/**
 * @internal
 */
const InControl$Aspects__symbol = ( /*#__PURE__*/Symbol('InControl.aspects'));
/**
 * @internal
 */
class InControl$Aspects {
    constructor(control, aspects) {
        this.control = control;
        this.aspects = aspects;
        this._byKey = new Map();
    }
    aspect(aspect) {
        const applied = this._byKey.get(aspect);
        if (applied) {
            // Aspect applied already.
            // Return it.
            return applied;
        }
        const application = this.control._applyAspect(aspect)
            || aspect.applyTo(this.control);
        this._byKey.set(aspect, application);
        return application;
    }
}

/**
 * User input control.
 *
 * Maintains input value and various aspects of the user input, such as input focus, validity, etc.
 *
 * @category Control
 * @typeParam TValue - Input value type.
 */
class InControl extends ValueTracker {
    /**
     * Constructs user input control.
     *
     * @param aspects - Input aspects applied by default. These are aspect converters to constructed control from the
     * {@link inValueOf same-valued one}.
     */
    constructor({ aspects, } = {}) {
        super();
        const aspectList = arrayOfElements(aspects);
        this[InControl$Aspects__symbol] = new InControl$Aspects(this, aspectList.length
            ? intoConvertedAspects(aspectList)(inValueOf(this), this)
            : noopInConversion);
    }
    /**
     * Retrieves an aspect instance applied to this control.
     *
     * If the given `aspect` is not applied yet, then applies it first.
     *
     * @typeParam TInstance - Aspect instance type.
     * @typeParam TKind - Aspect application kind.
     * @param aspectKey - A key of aspect to apply to this control.
     *
     * @returns An applied aspect instance.
     */
    aspect(aspectKey) {
        return this[InControl$Aspects__symbol].aspect(aspectKey[InAspect__symbol]).instance;
    }
    setup(aspectKeyOrSetup, aspectSetup) {
        if (isAspectKey(aspectKeyOrSetup)) {
            aspectSetup(this.aspect(aspectKeyOrSetup), this);
        }
        else {
            aspectKeyOrSetup(this);
        }
        return this;
    }
    convert(by, ...and) {
        return new InControl$Converted(this, intoConvertedBy(by, ...and));
    }
    /**
     * Applies the given aspect to this control in a custom way.
     *
     * @typeParam TInstance - Aspect instance type.
     * @typeParam TKind - Aspect application kind.
     * @param aspect - An aspect to apply.
     *
     * @returns Either applied aspect instance or `undefined` to apply the aspect in standard way (i.e. using
     * `InAspect.applyTo()` method).
     */
    _applyAspect(aspect) {
        return this[InControl$Aspects__symbol].aspects.applyAspect(aspect);
    }
}
/**
 * @internal
 */
class InControl$SameValued extends InControl {
    constructor(_control) {
        super();
        this._control = _control;
    }
    get supply() {
        return this._supply || (this._supply = new Supply(noop).needs(this._control));
    }
    get it() {
        return this._control.it;
    }
    set it(value) {
        this._control.it = value;
    }
    get on() {
        return this._control.on;
    }
}
/**
 * Constructs input control with the same value as another one.
 *
 * The constructed control does not inherit any aspects from original one.
 *
 * @category Control
 * @typeParam TValue - Input value type.
 * @param control - Original control containing the value.
 *
 * @returns New input control that accesses the value of original `control`.
 */
function inValueOf(control) {
    return new InControl$SameValued(control);
}
class InControl$Converted extends InControl {
    constructor(src, by) {
        super();
        this._on = new EventEmitter();
        this.supply = new Supply().needs(src);
        let lastRev = 0;
        let backward;
        const conversion = by(src, this);
        let set;
        let get;
        let convertAspect;
        if ( /*#__INLINE__*/isInAspectConversion(conversion)) {
            set = asis;
            get = asis;
            convertAspect = (aspect) => {
                const fallback = src[InControl$Aspects__symbol].aspect(aspect);
                return fallback.attachTo ? fallback.attachTo(this) : fallback.convertTo(this);
            };
        }
        else {
            set = conversion.set;
            get = conversion.get;
            convertAspect = (aspect) => {
                const fallback = src[InControl$Aspects__symbol].aspect(aspect);
                return fallback.convertTo(this);
            };
        }
        this._applyAspect = aspect => { var _a; return ((_a = conversion.applyAspect) === null || _a === void 0 ? void 0 : _a.call(conversion, aspect)) || convertAspect(aspect); };
        this._it = trackValue([set(src.it), 0]);
        this._it.supply.needs(this.supply);
        this._it.on(([newValue], [oldValue]) => {
            if (newValue !== oldValue) {
                this._on.send(newValue, oldValue);
            }
        }).cuts(this._on);
        src.on(value => {
            if (value !== backward) {
                this._it.it = [set(value), ++lastRev];
            }
        }).cuts(this);
        this._it.on(([value, rev]) => {
            if (rev !== lastRev) {
                lastRev = rev;
                backward = get(value);
                try {
                    src.it = backward;
                }
                finally {
                    backward = undefined;
                }
            }
        });
    }
    get it() {
        return this._it.it[0];
    }
    set it(value) {
        const [prevValue, prevRev] = this._it.it;
        if (value !== prevValue) {
            this._it.it = [value, prevRev + 1];
        }
    }
    get on() {
        return this._on.on;
    }
}

/**
 * @internal
 */
const InContainer__aspect = {
    applyTo(control) {
        return inconvertibleInAspect(control, InContainer);
    },
};
/**
 * An input control containing other controls.
 *
 * Container is available as an aspect of itself and converted controls with the same value.
 *
 * @category Control
 * @typeParam TValue - Input value type.
 */
class InContainer extends InControl {
    static get [InAspect__symbol]() {
        return InContainer__aspect;
    }
    _applyAspect(aspect) {
        return aspect === InContainer__aspect
            ? inconvertibleInAspect(this, InContainer, this)
            : super._applyAspect(aspect);
    }
}
/**
 * Controls of input container.
 *
 * Allows to track container contents. I.e. nested controls, their additions and removal.
 *
 * Implements `EventSender` interface by sending arrays of added and removed control entries.
 *
 * Implements `EventKeeper` interface by sending container contents instance each time it is changed.
 *
 * @category Control
 */
class InContainerControls {
    [OnEvent__symbol]() {
        return this.on;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}

/**
 * @internal
 */
const InParents__aspect = {
    applyTo(control) {
        return knownInAspect(new InControlParents(control));
    },
};
/**
 * Parents of input control.
 *
 * Reflects all containers the control belongs to. Note that component may belong to multiple containers. Or even
 * to the same container multiple times.
 *
 * Implements `EventSender` interface by sending arrays of parent entries the control is added to and removed from.
 *
 * Implements `EventKeeper` interface by sending a snapshot of all parents each time it is updated.
 *
 * @category Aspect
 */
class InParents {
    static get [InAspect__symbol]() {
        return InParents__aspect;
    }
    [OnEvent__symbol]() {
        return this.on;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}
/**
 * @internal
 */
class InControlParents extends InParents {
    constructor(_control) {
        super();
        this._control = _control;
        this._map = new Map();
        this._on = new EventEmitter();
        this._on.supply.needs(this._control);
        const allParents = () => ({
            [Symbol.iterator]: () => this._map.keys(),
        });
        this.read = this.on.do(mapAfter(allParents, allParents));
    }
    add(entry) {
        const existingSupply = this._map.get(entry);
        if (existingSupply) {
            // Parent entry already added. Doing nothing
            return existingSupply;
        }
        // Adding new entry
        const supply = new Supply(() => {
            this._map.delete(entry);
            this._on.send([], [entry]);
        });
        this._map.set(entry, supply);
        this._on.send([entry], []);
        return supply
            .needs(this._control)
            .needs(entry.parent);
    }
    get on() {
        return this._on.on;
    }
}

/**
 * @internal
 */
const InElement__aspect = {
    applyTo(control) {
        return inconvertibleInAspect(control, InElement);
    },
};
/**
 * HTML input element control.
 *
 * It is also available as aspect of itself and converted controls with the same value.
 *
 * @category Control
 * @typeParam TValue - Input value type.
 * @typeParam TElt - A type of input HTML element.
 */
class InElement extends InControl {
    static get [InAspect__symbol]() {
        return InElement__aspect;
    }
    _applyAspect(aspect) {
        return aspect === InElement__aspect
            ? inconvertibleInAspect(this, InElement, this)
            : super._applyAspect(aspect);
    }
}

/**
 * @internal
 */
const InMode__aspect = {
    applyTo(control) {
        return builtInAspect(control, InMode, ctrl => new InControlMode(ctrl));
    },
};
/**
 * Input mode aspect of control.
 *
 * Control can be either enabled, disabled, or readonly.
 *
 * Each control maintains its own state, while nested controls respect container ones. I.e. when container is disabled
 * all nested ones are also disabled. When container is readonly, all nested ones are also readonly, unless explicitly
 * disabled.
 *
 * When applied to input element this aspect maintains its `disabled` and `readonly` attributes (not properties!).
 *
 * Implements `EventSender` interface by sending new and old mode values on each update.
 *
 * Implements `EventKeeper` interface by sending actual mode value.
 *
 * @category Aspect
 */
class InMode {
    static get [InAspect__symbol]() {
        return InMode__aspect;
    }
    [OnEvent__symbol]() {
        return this.on;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
    /**
     * Checks whether control in the given `mode` has data to submit.
     *
     * @param mode - Input control mode to check.
     *
     * @returns `true` if control in the given `mode` has data to submit, or `false` otherwise.
     */
    static hasData(mode) {
        return mode !== 'off' && mode[0] !== '-';
    }
    /**
     * Unregisters all receivers.
     *
     * @param reason - Optional reason.
     *
     * @returns `this` instance.
     */
    done(reason) {
        this.own.supply.off(reason);
        return this;
    }
}
/**
 * @internal
 */
class OwnModeTracker extends ValueTracker {
    constructor(element) {
        super();
        this._tracker = trackValue(element ? initialInMode(element.element) : 'on');
    }
    get supply() {
        return this._tracker.supply;
    }
    get it() {
        return this._tracker.it;
    }
    set it(value) {
        switch (value) {
            case 'off':
            case 'ro':
            case '-on':
            case '-ro':
                break;
            default:
                value = 'on'; // Correct the value.
        }
        this._tracker.it = value;
    }
    get on() {
        return this._tracker.on;
    }
}
/**
 * @internal
 */
class DerivedInModes {
    constructor() {
        this._all = new Set();
        this._on = new EventEmitter();
        const sources = afterSent(this._on.on.do(mapOn(() => this._all)), valuesProvider(this._all));
        this.read = sources.do(digAfter_((set) => afterEach(...set)), mapAfter(mergeInModes));
    }
    add(source) {
        const supply = new Supply(() => {
            this._all.delete(source);
            this._on.send();
        });
        this._all.add(source);
        this._on.send();
        return supply;
    }
}
/**
 * @internal
 */
class InControlMode extends InMode {
    constructor(_control) {
        super();
        this._control = _control;
        this._derived = new DerivedInModes();
        const element = _control.aspect(InElement);
        this.own = new OwnModeTracker(element);
        this.own.supply.needs(_control);
        this.derive(_control.aspect(InParents).read.do(digAfter(parentsInMode)));
        let last = 'on';
        this.read = afterAll({
            derived: this._derived.read,
            own: this.own,
        }).do(translateAfter((send, { derived: [derived], own: [own], }) => {
            let next;
            if (own === 'off' || derived === 'off') {
                next = 'off';
            }
            else {
                let off = false;
                if (own[0] === '-') {
                    off = true;
                    own = own.substring(1);
                }
                if (derived[0] === '-') {
                    off = true;
                    derived = derived.substring(1);
                }
                next = derived === 'ro' ? 'ro' : own;
                if (off) {
                    next = '-' + next;
                }
            }
            if (last !== next) {
                send(last = next);
            }
        }, valuesProvider(last)));
        let lastUpdate = 'on';
        this.on = this.read.do(translateOn((send, value) => {
            const old = lastUpdate;
            if (old !== value) {
                send(lastUpdate = value, old);
            }
        }));
        if (element) {
            this.read(value => applyInMode(element.element, value));
        }
    }
    derive(source) {
        return this._derived.add(afterSupplied(isEventKeeper(source) ? source : source(this._control)).do(supplyAfter(this._control))).needs(this._control);
    }
}
/**
 * @internal
 */
function initialInMode(element) {
    return element.getAttribute('disabled') != null
        ? 'off'
        : (element.getAttribute('readonly') != null ? 'ro' : 'on');
}
/**
 * @internal
 */
function applyInMode(element, value) {
    switch (value) {
        case 'off':
            element.setAttribute('disabled', '');
            break;
        case 'ro':
        case '-ro':
            // Workaround of https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/12087679/
            element.setAttribute('disabled', '');
            element.removeAttribute('disabled');
            element.setAttribute('readonly', '');
            break;
        default:
            // Workaround of https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/12087679/
            element.setAttribute('disabled', '');
            element.removeAttribute('disabled');
            // Workaround of https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/12087679/
            element.setAttribute('readonly', '');
            element.removeAttribute('readonly');
    }
}
/**
 * @internal
 */
function parentsInMode(parents) {
    const parentList = itsElements(parents);
    if (!parentList.length) {
        return afterThe('on');
    }
    const parentModes = parentList.map(({ parent }) => parent.aspect(InMode));
    return afterEach(...parentModes).do(mapAfter(mergeInModes));
}
/**
 * @internal
 * @param modes
 */
function mergeInModes(...modes) {
    return inModeValue(...overElementsOf(...modes));
}
/**
 * Merges multiple input mode values.
 *
 * @category Aspect
 * @param modes - Input mode values to merge.
 *
 * @returns Merged input mode value.
 */
function inModeValue(...modes) {
    let ro = false;
    let off = false;
    for (const mode of modes) {
        switch (mode) {
            case 'off':
                return 'off';
            case 'ro':
                ro = true;
                break;
            case '-on':
                off = true;
                break;
            case '-ro':
                off = true;
                ro = true;
                break;
        }
    }
    return off ? (ro ? '-ro' : '-on') : (ro ? 'ro' : 'on');
}

/**
 * @internal
 */
const InData__aspect = {
    applyTo(control) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return builtInAspect(control, InData, (ctrl) => afterAll({
            value: ctrl,
            mode: ctrl.aspect(InMode),
        }).do(mapAfter(({ value: [value], mode: [mode] }) => InMode.hasData(mode)
            ? value
            : undefined)));
    },
};
/**
 * @category Aspect
 */
const InData = {
    get [InAspect__symbol]() {
        return InData__aspect;
    },
};

/**
 * @internal
 */
const InGroup__aspect = {
    applyTo(control) {
        return inconvertibleInAspect(control, InGroup);
    },
};
/**
 * A group of input controls.
 *
 * Nested controls are identified by keys and can be added and removed via `controls` property.
 *
 * Group value (called model) is an object formed by nested control values. The model property value is the one of the
 * control with the same key, if present. When model is updated corresponding controls are also updated.
 *
 * Group is available as an aspect of itself and converted controls with the same value.
 *
 * @category Control
 * @typeParam TModel - Group model type, i.e. its value type.
 */
class InGroup extends InContainer {
    static get [InAspect__symbol]() {
        return InGroup__aspect;
    }
    _applyAspect(aspect) {
        return aspect === InGroup__aspect
            ? inconvertibleInAspect(this, InGroup, this)
            : super._applyAspect(aspect);
    }
}
/**
 * Input group controls.
 *
 * @category Control
 * @typeParam TModel - Group model type, i.e. its value type.
 */
class InGroupControls extends InContainerControls {
    /**
     * Removes input control with the given key.
     *
     * Calling this method is the same as calling `set(key, undefined)`
     *
     * @param key - A key of input control to remove. I.e. corresponding model property key.
     */
    remove(key) {
        this.set(key, undefined);
    }
}
/**
 * @internal
 */
const inControlReplacedReason$1 = {};
/**
 * @internal
 */
class InGroupSnapshot {
    constructor(_map) {
        this._map = _map;
        this._it = mapIt(overIterator(() => this._map.values()), ([control]) => control);
        this._entriesIt = mapIt(this._map, ([key, [control]]) => [key, control]);
    }
    get(key) {
        const entry = this._map.get(key);
        return entry && entry[0];
    }
    [Symbol.iterator]() {
        return this[PushIterator__symbol]();
    }
    [PushIterator__symbol](accept) {
        return this._it[PushIterator__symbol](accept);
    }
    entries() {
        return iteratorOf(this._entriesIt);
    }
}
/**
 * @internal
 */
class InGroupMap {
    constructor(_controls) {
        this._controls = _controls;
        this._supply = new Supply();
        this._map = new Map();
    }
    set(key, control, added, removed) {
        const replaced = this._map.get(key);
        let supply;
        if (control) {
            supply = new Supply();
            const entry = this.newEntry(key, control, supply);
            let sendUpdate = true;
            if (replaced) {
                if (replaced[0] === control) {
                    // Do not send update when replacing control with itself
                    sendUpdate = false;
                }
                else {
                    removed.push([key, replaced]);
                }
            }
            if (sendUpdate) {
                this.modify().set(key, entry);
                added.push([key, entry]);
            }
            else {
                this._map.set(key, entry);
            }
        }
        else {
            supply = neverSupply();
            if (replaced) {
                removed.push([key, replaced]);
                this.modify().delete(key);
            }
        }
        if (replaced) {
            replaced[1].off(inControlReplacedReason$1);
        }
        return supply;
    }
    newEntry(key, control, supply) {
        return [
            control,
            new Supply(reason => {
                if (reason !== inControlReplacedReason$1) {
                    this._controls.remove(key);
                }
            })
                .needs(this._supply)
                .needs(supply)
                .whenOff(reason => supply.off(reason === inControlReplacedReason$1 ? undefined : reason)),
        ];
    }
    modify() {
        if (this._shot) {
            const map = new Map();
            itsEach(this._map.entries(), ([k, e]) => map.set(k, e));
            this._shot = undefined;
            this._map = map;
        }
        return this._map;
    }
    snapshot() {
        return this._shot || (this._shot = new InGroupSnapshot(this._map));
    }
    clear() {
        const added = [];
        const removed = [];
        itsEach(this._map.keys(), key => this.set(key, undefined, added, removed));
        return removed;
    }
}
/**
 * @internal
 */
class InGroupControlControls extends InGroupControls {
    constructor(_group) {
        super();
        this._group = _group;
        this._updates = new EventEmitter();
        const applyModelToControls = (model) => {
            this.read.do(onceAfter)(snapshot => {
                const withValues = new Set();
                itsEach(overEntries(model), ([key, value]) => {
                    withValues.add(key);
                    const control = snapshot.get(key);
                    if (control) {
                        control.it = value;
                    }
                });
                itsEach(snapshot.entries(), ([key, control]) => {
                    if (!withValues.has(key)) {
                        control.it = undefined;
                    }
                });
            });
        };
        this._map = new InGroupMap(this);
        this.on = this._updates.on.do(translateOn((send, added, removed) => send(added.map(controlEntryToGroupEntry), removed.map(controlEntryToGroupEntry))));
        const takeSnapshot = this._map.snapshot.bind(this._map);
        this.read = this._updates.on.do(mapAfter(takeSnapshot, takeSnapshot));
        this._map._supply.needs(_group.read(applyModelToControls));
    }
    set(keyOrControls, newControl) {
        const group = this._group;
        const added = [];
        const removed = [];
        let supply;
        if (typeof keyOrControls === 'object') {
            supply = new Supply();
            itsEach(overEntries(keyOrControls), ([key, value]) => {
                this._map.set(key, value, added, removed).needs(supply);
            });
        }
        else {
            supply = this._map.set(keyOrControls, newControl, added, removed);
        }
        if (added.length || removed.length) {
            this._updates.send(added, removed);
            if (added.length) {
                applyControlsToModel();
            }
        }
        return supply;
        function applyControlsToModel() {
            let newModel;
            added.forEach((keyAndEntry) => {
                const [key, [control, supply]] = keyAndEntry;
                control.aspect(InParents).add({ parent: group }).as(supply);
                const value = control.it;
                if (newModel) {
                    newModel[key] = value;
                }
                else {
                    const model = group.it;
                    if (model[key] !== value) {
                        newModel = { ...model, [key]: value };
                    }
                }
            });
            if (newModel) {
                group.it = newModel;
            }
            added.forEach((keyAndEntry) => {
                const [key, [control, supply]] = keyAndEntry;
                control.read.do(supplyAfter(supply))(value => {
                    if (group.it[key] !== value) {
                        group.it = {
                            ...group.it,
                            [key]: value,
                        };
                    }
                }).cuts(supply);
            });
        }
    }
    clear() {
        const removed = this._map.clear();
        if (removed.length) {
            this._updates.send([], removed);
        }
    }
}
/**
 * @internal
 */
function controlEntryToGroupEntry([key, [control]]) {
    return [key, control];
}
/**
 * @internal
 */
class InGroupControl extends InGroup {
    constructor(model, opts) {
        super(opts);
        this._model = trackValue(model);
        this.controls = new InGroupControlControls(this);
        this.supply.whenOff(() => this.controls.clear());
    }
    get supply() {
        return this._model.supply;
    }
    get it() {
        return this._model.it;
    }
    set it(value) {
        this._model.it = value;
    }
    get on() {
        return this._model.on;
    }
    _applyAspect(aspect) {
        if (aspect === InData[InAspect__symbol]) {
            return {
                instance: inGroupData(this),
                convertTo: noop,
            };
        }
        return super._applyAspect(aspect);
    }
}
/**
 * @internal
 */
function inGroupData(group) {
    return afterAll({
        cs: group.controls,
        model: group,
        mode: group.aspect(InMode),
    }).do(digAfter_(readInGroupData));
}
/**
 * @internal
 */
function readInGroupData({ cs: [controls], model: [model], mode: [mode], }) {
    if (!InMode.hasData(mode)) {
        return afterThe();
    }
    const csData = {};
    itsEach(controls.entries(), ([key, control]) => {
        csData[key] = control.aspect(InData);
    });
    return afterAll(csData).do(mapAfter(controlsData => {
        const data = { ...model };
        itsEach(overEntries(controlsData), (keyAndControlData) => {
            const [key, [controlData]] = keyAndControlData;
            data[key] = controlData;
        });
        return data;
    }));
}
/**
 * Constructs input controls group.
 *
 * @category Control
 * @typeParam TModel - Group model type, i.e. its value type.
 * @param model - Initial model of the group.
 * @param aspects - Input aspects applied by default. These are aspect converters to constructed control
 * from {@link inValueOf same-valued one}.
 *
 * @returns New input controls group.
 */
function inGroup(model, { aspects, } = {}) {
    return new InGroupControl(model, { aspects });
}

/**
 * @internal
 */
class InValueControl extends InControl {
    constructor(initial, opts) {
        super(opts);
        this._it = trackValue(initial);
    }
    get supply() {
        return this._it.supply;
    }
    get it() {
        return this._it.it;
    }
    set it(value) {
        this._it.it = value;
    }
    get on() {
        return this._it.on;
    }
}
/**
 * Constructs simple input control.
 *
 * This control does not handle actual user input. Instead, it maintains the value set programmatically.
 *
 * @category Control
 * @typeParam TValue - Input value type.
 * @param initial - Initial input value.
 * @param aspects - Input aspects applied by default. These are aspect converters to constructed control
 * from the {@link inValueOf same-valued one}.
 *
 * @returns New input control.
 */
function inValue(initial, { aspects, } = {}) {
    return new InValueControl(initial, { aspects });
}

/**
 * @internal
 */
const InList__aspect = {
    applyTo(control) {
        return inconvertibleInAspect(control, InList);
    },
};
/**
 * An indexed list of input controls.
 *
 * Nested controls can be added and removed via `controls` property.
 *
 * List value (called model) is an array object formed by nested control values. The item property value is the one
 * of the control with the same index, if present. When model is updated corresponding controls are also updated.
 *
 * List is available as an aspect of itself and converted controls with the same value.
 *
 * @category Control
 * @typeParam TItem - Model item type.
 */
class InList extends InContainer {
    static get [InAspect__symbol]() {
        return InList__aspect;
    }
    _applyAspect(aspect) {
        return aspect === InList__aspect
            ? inconvertibleInAspect(this, InList, this)
            : super._applyAspect(aspect);
    }
}
/**
 * Input list controls.
 *
 * @category Control
 * @typeParam TItem - Model item type.
 */
class InListControls extends InContainerControls {
    /**
     * Sets input control with the given index.
     *
     * Replaces existing control if already present.
     *
     * @param index - An index of input control to set. I.e. corresponding model item index.
     * @param control - Input control to add.
     *
     * @returns A supply of just added control that removes it once cut off.
     */
    set(index, control) {
        return this.splice(index, 1, control);
    }
    /**
     * Inserts input controls at the given position.
     *
     * @param index - An index to insert controls at.
     * @param controls - Input controls to add after the last one.
     *
     * @returns A supply of just inserted controls that removes them once cut off. Cut off supply when no controls
     * inserted.
     */
    insert(index, ...controls) {
        return this.splice(index, 0, ...controls);
    }
    /**
     * Removes input controls starting at the given index.
     *
     * @param start - An index of the first control to remove.
     * @param end - An index of the control next to the last one to remove. Only one control will be removed if omitted.
     */
    remove(start, end) {
        this.splice(start, end == null ? 1 : end - start);
    }
    /**
     * Removes all input controls.
     */
    clear() {
        this.splice(0);
    }
}
/**
 * @internal
 */
class InListSnapshot {
    constructor(_entries) {
        this._entries = _entries;
        this._it = mapArray(this._entries, ([control]) => control);
        this._entriesIt = overIterator(() => iteratorOf(this._entries.map(([entry], index) => [index, entry])));
    }
    get length() {
        return this._entries.length;
    }
    [Symbol.iterator]() {
        return this[PushIterator__symbol]();
    }
    [PushIterator__symbol](accept) {
        return this._it[PushIterator__symbol](accept);
    }
    entries() {
        return iteratorOf(this._entriesIt);
    }
    item(index) {
        const entry = this._entries[index];
        return entry && entry[0];
    }
}
/**
 * @internal
 */
const inControlReplacedReason = {};
/**
 * @internal
 */
class InListEntries {
    constructor(_controls, initial) {
        this._controls = _controls;
        this._supply = new Supply();
        this._entries = initial.map(control => inListEntry(this, control));
    }
    splice(start, deleteCount, controls, added, removed) {
        let supply = controls.length > 1 ? new Supply() : undefined;
        const modify = () => {
            if (this._shot) {
                this._shot = undefined;
                this._entries = this._entries.slice();
            }
            return this._entries;
        };
        const extracted = deleteCount == null
            ? modify().splice(start)
            : modify().splice(start, deleteCount, ...controls.map((control, index) => {
                const entry = inListEntry(this, control);
                added.push([start + index, entry]);
                if (supply) {
                    entry[1].needs(supply);
                }
                else {
                    supply = entry[1];
                }
                return entry;
            }));
        removed.push(...extracted.map((entry, index) => {
            entry[1].off(inControlReplacedReason);
            return [start + index, entry];
        }));
        if (!supply) {
            return neverSupply();
        }
        const result = new Supply();
        supply.needs(result).whenOff(reason => result.off(reason !== inControlReplacedReason ? reason : undefined));
        return result;
    }
    snapshot() {
        return this._shot || (this._shot = new InListSnapshot(this._entries));
    }
}
/**
 * @internal
 */
function inListEntry(entries, control) {
    const entry = [
        control,
        new Supply(reason => {
            if (reason !== inControlReplacedReason) {
                entries._controls.remove(entries._entries.findIndex(e => e === entry));
            }
        }).needs(entries._supply),
    ];
    return entry;
}
/**
 * @internal
 */
function readControlValue(controls, [control, supply]) {
    control.aspect(InParents).add({ parent: controls._list }).as(supply);
    control.read({
        supply,
        receive: (_ctx, value) => {
            const index = controls._entries._entries.findIndex(([ctrl]) => ctrl === control);
            const model = controls._list.it;
            if (model[index] !== value) {
                const newModel = controls._list.it.slice();
                newModel[index] = control.it;
                controls._list.it = newModel;
            }
        },
    });
}
/**
 * @internal
 */
class InListControlControls extends InListControls {
    constructor(_list) {
        super();
        this._list = _list;
        this._updates = new EventEmitter();
        this._entries = new InListEntries(this, inListControlsByModel(_list.it, 0));
        const applyModelToControls = {
            receive: (context, model) => {
                context.onRecurrent(noop);
                const entries = this._entries._entries;
                model.forEach((item, index) => {
                    const entry = entries[index];
                    if (entry) {
                        entry[0].it = item;
                    }
                });
                if (model.length < entries.length) {
                    // Remove controls without values in model
                    this.splice(model.length);
                }
                else if (model.length > entries.length) {
                    // Create missing value controls
                    this.add(...inListControlsByModel(model, entries.length));
                }
            },
        };
        this._entries._supply.needs(_list.read(applyModelToControls))
            .needs(this._list)
            .cuts(this._updates);
        this.on = this._updates.on.do(translateOn((send, added, removed) => send(added.map(toInListEntry), removed.map(toInListEntry))));
        const takeSnapshot = this._entries.snapshot.bind(this._entries);
        this.read = this._updates.on.do(mapAfter(takeSnapshot, takeSnapshot));
        this._entries._entries.forEach(entry => readControlValue(this, entry));
    }
    add(...controls) {
        return this.splice(this._entries._entries.length, 0, ...controls);
    }
    splice(start, deleteCount, ...controls) {
        const list = this._list;
        const added = [];
        const removed = [];
        const supply = this._entries.splice(start, deleteCount, controls, added, removed);
        if (added.length || removed.length) {
            const updated = list.it.slice();
            updated.splice(start, removed.length, ...added.map(([, [control]]) => control.it));
            list.it = updated;
            this._updates.send(added, removed);
            added.forEach(([, entry]) => readControlValue(this, entry));
        }
        return supply;
    }
}
/**
 * @internal
 */
function inListControlsByModel(model, start) {
    const controls = [];
    for (let i = start; i < model.length; ++i) {
        controls.push(inValue(model[i]));
    }
    return controls;
}
/**
 * @internal
 */
function toInListEntry([key, [control]]) {
    return [key, control];
}
/**
 * @internal
 */
class InListControl extends InList {
    constructor(model, opts) {
        super(opts);
        this._model = trackValue(model);
        this.controls = new InListControlControls(this);
        this.supply.whenOff(() => this.controls.clear());
    }
    get supply() {
        return this._model.supply;
    }
    get it() {
        return this._model.it;
    }
    set it(value) {
        this._model.it = value;
    }
    get on() {
        return this._model.on;
    }
    _applyAspect(aspect) {
        if (aspect === InData[InAspect__symbol]) {
            return {
                instance: inListData(this),
                convertTo: noop,
            };
        }
        return super._applyAspect(aspect);
    }
}
/**
 * @internal
 */
function inListData(list) {
    return afterAll({
        cs: list.controls,
        mode: list.aspect(InMode),
    }).do(digAfter_(readInListData));
}
/**
 * @internal
 */
function readInListData({ cs: [controls], mode: [mode], }) {
    if (!InMode.hasData(mode)) {
        return afterThe();
    }
    const csData = mapIt(controls, control => control.aspect(InData));
    return afterEach(...csData).do(mapAfter((...controlsData) => controlsData.map(([d]) => d).filter(isDefined)));
}
/**
 * Constructs input controls list.
 *
 * @category Control
 * @typeParam TItem - Model item type.
 * @param model - Initial model of the list.
 * @param aspects - Input aspects applied by default. These are aspect converters to constructed control
 * from {@link inValueOf same-valued one}.
 *
 * @returns New input controls group.
 */
function inList(model, { aspects, } = {}) {
    return new InListControl(model, { aspects });
}

/**
 * Creates a converter that converts an input control to the one replacing `undefined` value with fallback one.
 *
 * Treats `null` values as `undefined`. Despite the signature does not allow nulls they are often used instead.
 * E.g. when receiving JSON from server.
 *
 * @category Converter
 * @typeParam TValue - Input value type.
 * @param fallback - A fallback value that is used instead of original one when `undefined` (or`null`) is assigned
 * to converted control.
 */
function intoFallback(fallback) {
    return () => ({
        set: asis,
        get(value) {
            return value != null ? value : fallback;
        },
    });
}

/**
 * @internal
 */
const RequireNothing$ = ( /*#__PURE__*/afterThe());
/**
 * Input validator that requires nothing.
 *
 * @category Validation
 */
function requireNothing() {
    return RequireNothing$;
}

/**
 * Converts arbitrary input validator to normalized form.
 *
 * @category Validation
 * @typeParam TValue - Input value type.
 * @param validator - Validator to convert.
 *
 * @returns A function accepting input control as its only parameter and returning an `AfterEvent` keeper of validation
 * messages.
 */
function inValidator(validator) {
    if (isEventKeeper(validator)) {
        return valueProvider(afterSupplied(validator));
    }
    if (typeof validator === 'function') {
        return control => afterSupplied(validator(control));
    }
    return control => control.read.do(translateAfter(send => send(...arrayOfElements(validator.validate(control)))));
}

const dontRemove = {};
/**
 * @internal
 */
class InValidationMessages {
    constructor(control) {
        const emitter = new EventEmitter();
        const validators = new Map();
        const validatorMessages = new Map();
        // Sends validation messages
        let send = noop;
        // Validates using the given validator
        let validate = noop;
        this._messages = afterEventBy(receiver => {
            // Validation messages supply
            const resultSupply = afterSupplied(emitter, valuesProvider())(receiver).whenOff(() => {
                send = noop; // Disable message sending
                validate = noop; // Disable validation
            });
            // Enable validation using the given validator
            validate = (validator, validatorSupply) => {
                const supply = validator((...messages) => {
                    if (messages.length) {
                        // Replace messages reported by validator.
                        validatorMessages.set(validator, messages);
                    }
                    else if (!validatorMessages.delete(validator)) {
                        // Nothing removed. No need to send messages
                        return;
                    }
                    send(); // Send all messages.
                })
                    .needs(validatorSupply)
                    .whenOff(reason => {
                    if (reason !== dontRemove) {
                        validatorSupply.off(reason);
                    }
                    if (validatorMessages.delete(validator)) {
                        // Send all messages only if the removed validator reported some messages earlier
                        send();
                    }
                });
                resultSupply.whenOff(() => supply.off(dontRemove));
            };
            // Enable each validator
            itsEach(validators.entries(), ([validator, validatorSupply]) => validate(validator, validatorSupply));
            // Enable message sending
            send = () => {
                emitter.send(...allMessages());
            };
            // Send messages if present already
            if (validatorMessages.size) {
                send();
            }
        }).do(shareAfter, supplyAfter(control));
        this.from = validator => {
            const source = inValidator(validator)(control);
            const validatorSupply = new Supply(() => {
                validators.delete(source);
            });
            validators.set(source, validatorSupply);
            validate(source, validatorSupply); // Start validation using validator
            return validatorSupply.needs(control);
        };
        function allMessages() {
            return flatMapIt(validatorMessages.values());
        }
    }
    [AfterEvent__symbol]() {
        return this._messages;
    }
}

/**
 * Creates input validator that validates using all listed validators.
 *
 * @category Validation
 * @typeParam TValue - Input value type.
 * @param validators - Validators to validate the input with.
 *
 * @returns Validator that requires all the given `validators`. Or just the given validator if it is the only one given.
 */
function requireAll(...validators) {
    const numValidators = validators.length;
    if (numValidators === 1) {
        return validators[0];
    }
    if (!numValidators) {
        return requireNothing;
    }
    return (control) => {
        const messages = new InValidationMessages(control);
        validators.forEach(validator => messages.from(validator));
        return messages;
    };
}

/**
 * Creates input validator that applies requirements on input text length.
 *
 * Ignores empty input. Reports too short values with `incomplete` and `tooShort` message codes. Reports too long values
 * with `invalid` and `tooLong` message codes.
 *
 * @category Validation
 * @param min - Required minimum length. Not checked if omitted.
 * @param max - Required maximum length. Not checked if omitted.
 *
 * @returns Element validator applicable to text input control.
 */
function requireLength(min, max) {
    return {
        validate({ it: { length } }) {
            if (length) {
                if (min && length < min) {
                    return { incomplete: 'tooShort', tooShort: min };
                }
                if (max != null && length > max) {
                    return { invalid: 'tooLong', tooLong: max };
                }
            }
            return;
        },
    };
}

/**
 * @internal
 */
const InValidation__aspect = {
    applyTo(control) {
        return builtInAspect(control, InValidation, (ctrl, origin) => {
            const validation = new InControlValidation(ctrl);
            if (origin) {
                validation.by(origin.aspect(InValidation).read.do(translateAfter((send, result) => send(...result.messages()))));
            }
            return validation;
        });
    },
};
/**
 * Validation aspect of the input.
 *
 * Reports validation messages sent by registered validators. To register validator call a `InValidation.by()` method.
 *
 * Implements an `EventKeeper` interface by sending validation result whenever validation messages reported.
 *
 * A validation aspect of converted control reports all messages from original control in addition to its own.
 *
 * A validation aspect of input controls container reports all messages from nested controls in addition to its own.
 *
 * @category Aspect
 * @typeParam TValue - Input value type.
 */
class InValidation {
    /**
     * Input validation aspect.
     */
    static get [InAspect__symbol]() {
        return InValidation__aspect;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}
/**
 * @internal
 */
const noInValidationErrors = {
    get ok() {
        return true;
    },
    messages() {
        return [];
    },
    has() {
        return false;
    },
    hasBut() {
        return false;
    },
    [Symbol.iterator]() {
        return overNone();
    },
    [PushIterator__symbol](_accept) {
        return overNone();
    },
};
/**
 * @internal
 */
class InValidationErrors {
    constructor(messages) {
        this._byCode = new Map();
        this._all = [];
        this._it = overArray(this._all);
        messages.forEach(message => {
            let nonEmpty = false;
            itsEach(overEntries(message), ([code, codePresent]) => {
                if (codePresent) {
                    nonEmpty = true;
                    const prev = this._byCode.get(code);
                    if (prev) {
                        prev.push(message);
                    }
                    else {
                        this._byCode.set(code, [message]);
                    }
                }
            });
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (nonEmpty) {
                this._all.push(message);
            }
        });
    }
    get ok() {
        return !this._all.length;
    }
    messages(code) {
        return code == null ? this._all : this._byCode.get(code) || [];
    }
    has(code) {
        return code == null || this._byCode.has(code);
    }
    hasBut(...codes) {
        return this._all.some(message => codes.every(code => !message[code]));
    }
    [Symbol.iterator]() {
        return this[PushIterator__symbol]();
    }
    [PushIterator__symbol](accept) {
        return this._it[PushIterator__symbol](accept);
    }
}
function inValidationResult(...messages) {
    return messages.length ? new InValidationErrors(messages) : noInValidationErrors;
}
/**
 * @internal
 */
class InControlValidation extends InValidation {
    constructor(control) {
        super();
        this._messages = new InValidationMessages(control);
        this.read = afterSupplied(this._messages).do(mapAfter(inValidationResult));
        const container = control.aspect(InContainer);
        if (container) {
            this._messages.from(nestedInValidationMessages(container));
        }
    }
    by(...validators) {
        return this._messages.from(requireAll(...validators));
    }
}
/**
 * @internal
 */
function nestedInValidationMessages(container) {
    return container.controls.read.do(digAfter_(controls => afterEach(...mapIt(controls, control => control.aspect(InValidation)))), translateAfter((send, ...results) => send(...flatMapArray(results, ([result]) => result))));
}

/**
 * Constructs input validator that filters validation messages from the given `validators` according to their codes.
 *
 * The validation messages reported by `validators` are filtered according to the following rules:
 * - If at least one message with `missing` code is reported, then strip out the ones with `incomplete` and `invalid`
 *   codes, except the ones with `despiteMissing` code.
 * - Otherwise, if at least one message with `incomplete` code is reported, then strip out the ones with `invalid`
 *   code, except the ones with `despiteIncomplete` code.
 * - Otherwise report all messages.
 *
 * @category Validation
 * @typeParam TValue - Input value type.
 * @param validators - Validators to validate the input with.
 *
 * @returns Validator that requires all the given `validators` and filters their output.
 */
function requireNeeded(...validators) {
    const validate = inValidator(requireAll(...validators));
    return (control) => validate(control).do(translateAfter(nextRequireNeededMessages));
}
/**
 * @internal
 */
function nextRequireNeededMessages(send, ...messages) {
    const result = inValidationResult(...messages);
    let filtered = result;
    if (result.has('missing')) {
        filtered = filterIt(result, 
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        message => !message.incomplete && !message.invalid || message.despiteMissing);
    }
    else if (result.has('incomplete')) {
        filtered = filterIt(result, 
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        message => !message.invalid || message.despiteIncomplete);
    }
    return send(...filtered);
}

function requirePresent(control) {
    return control
        ? control.read.do(translateAfter((send, value) => value ? send() : send({ missing: 'missing' })))
        : requirePresent;
}

/**
 * Creates input validator that applies requirements to numeric value range.
 *
 * Ignores NaN input. Reports too small values with `invalid` and `tooSmall` message codes. Reports too large values
 * with `invalid` and `tooLarge` message codes.
 *
 * @category Validation
 * @param min - Minimum allowed value, inclusive. Not checked if omitted.
 * @param max - Maximum allowed value, inclusive. Not checked if omitted.
 *
 * @returns Element validator applicable to numeric input control.
 */
function requireRange(min, max) {
    return {
        validate({ it }) {
            if (!isNaN(it)) {
                if (min != null && it < min) {
                    return { invalid: 'tooSmall', tooSmall: min };
                }
                if (max != null && it > max) {
                    return { invalid: 'tooLarge', tooLarge: max };
                }
            }
            return;
        },
    };
}

/**
 * @category Converter
 */
const InParser = {
    /**
     * Creates input control converter out of input text parser.
     *
     * @typeParam TValue - Parsed input value type.
     * @param parser - A parser to convert.
     *
     * @returns An input control converter that parses and formats text input.
     */
    converter(parser) {
        return (from, to) => {
            const [parse, format = String] = parser(from, to);
            const parseValidator = new EventEmitter();
            to.aspect(InValidation).by(afterSupplied(parseValidator, valuesProvider()));
            return {
                set(text) {
                    const errorList = [];
                    const parserErrors = {
                        report(...errors) {
                            errorList.push(...errors);
                        },
                    };
                    const result = parse(text, parserErrors);
                    parseValidator.send(...errorList);
                    return result;
                },
                get(value) {
                    const text = String(format(value));
                    parseValidator.send();
                    return text;
                },
            };
        };
    },
};
/**
 * Creates text input control converter that parses and formats input text with the given functions.
 *
 * @category Converter
 * @typeParam TValue - Parsed input value type.
 * @param parse - Text parser function. Accepts input text and parse errors as its parameters and returns parsed value.
 * @param format - Text formatter. Accepts value is its only parameter and returns formatted text. Standard to string
 * conversion is used if omitted.
 *
 * @returns New input converter.
 */
function intoParsedBy(parse, format) {
    return InParser.converter(valueProvider([parse, format]));
}

/**
 * @internal
 */
const notInteger = 'notInteger';
function intoInteger(radixOrFrom = 10, optTo) {
    if (typeof radixOrFrom !== 'number') {
        return intoInteger()(radixOrFrom, optTo);
    }
    return InParser.converter((_from, to) => [
        (text, errors) => {
            const result = parseInt(text, radixOrFrom);
            if (isNaN(result)) {
                errors.report({ invalid: notInteger, NaN: notInteger, notInteger });
            }
            return result;
        },
        value => (to.it = Math.floor(value)).toString(radixOrFrom),
    ]);
}

function intoTrimmed(_from, to) {
    if (!to) {
        return intoTrimmed;
    }
    return {
        set(value) {
            return value.trim();
        },
        get(value) {
            return to.it = value.trim();
        },
    };
}

/**
 * @internal
 */
const InSubmit__aspect = {
    applyTo(control) {
        return builtInAspect(control, InSubmit, ctrl => new InControlSubmit(ctrl));
    },
};
/**
 * Input submit error.
 *
 * Contains submit messages as validation result.
 *
 * @category Aspect
 */
class InSubmitError extends Error {
    /**
     * Constructs input submit error.
     *
     * @param errors - Input submit error messages. A `submit` code will be added to each of them, unless already present.
     */
    constructor(...errors) {
        super();
        this.errors = inValidationResult(...errors.map(message => message.submit ? message : { ...message, submit: true }));
    }
}
/**
 * Input submit rejection error.
 *
 * Raised if submit is not ready or in process already.
 *
 * @category Aspect
 */
class InSubmitRejectedError extends InSubmitError {
    /**
     * Constructs input submit rejection error.
     *
     * @param reason - A reason code.
     */
    constructor(reason) {
        super({ submit: 'rejected', rejected: reason, [reason]: true });
    }
}
/**
 * Input submit aspect.
 *
 * Allows to submit {@link InData input data} and reports submit status.
 *
 * Implements an `EventKeeper` interface by sending submit status flags to registered receivers.
 *
 * @category Aspect
 * @typeParam TValue - Input value type.
 */
class InSubmit {
    static get [InAspect__symbol]() {
        return InSubmit__aspect;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}
class InControlSubmit extends InSubmit {
    constructor(_control) {
        super();
        this._control = _control;
        this._flags = trackValue({ submitted: false, busy: false });
        this._errors = trackValue([]);
        this.read = afterAll({
            flags: this._flags,
            data: this._control.aspect(InData),
            messages: this._control.aspect(InValidation),
        }).do(supplyAfter(this._control), mapAfter(({ flags: [flags], data: [data], messages: [messages], }) => ({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            ready: data !== undefined && (messages.ok || itsEvery(messages, message => message.submit)),
            submitted: flags.submitted,
            busy: flags.busy,
        })));
        const validation = _control.aspect(InValidation);
        validation.by(this._errors.read.do(translateAfter((send, messages) => send(...messages))));
    }
    async submit(submitter) {
        if (this._control.supply.isOff) {
            throw new InSubmitRejectedError('noInput');
        }
        if (this._flags.it.busy) {
            throw new InSubmitRejectedError('busy');
        }
        const submit = this;
        const control = this._control;
        let errors;
        this._flags.it = { ...this._flags.it, submitted: true, busy: true };
        try {
            if (this._errors.it.length) {
                this._errors.it = [];
            }
            return await submitter(await submitData(), control);
        }
        catch (error) {
            errors = toInSubmitMessages(error);
            throw error;
        }
        finally {
            this._flags.it = { ...this._flags.it, busy: false };
            if (errors) {
                this._errors.it = errors;
            }
        }
        async function submitData() {
            const { data: [d], flags: [{ ready }] } = await afterAll({
                data: control.aspect(InData),
                flags: submit,
            });
            return ready
                ? d
                : Promise.reject(new InSubmitRejectedError('notReady'));
        }
    }
    reset() {
        const flags = this._flags.it;
        if (flags.submitted) {
            this._flags.it = { ...flags, submitted: false };
        }
        if (this._errors.it.length) {
            this._errors.it = [];
        }
    }
}
/**
 * @internal
 */
function toInSubmitMessages(error) {
    if (error instanceof InSubmitError) {
        return [...error.errors];
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return [{ submit: error }];
}

/**
 * Creates an {@link InMode input mode} source depending on the given `form` control status.
 *
 * This can be applied e.g. to {@link InSubmitButton submit button}, to {@link InFormElement form element},
 * or to input controls. The defaults best suit the latter.
 *
 * @category Aspect
 * @param form - Form control the evaluated mode depends on. Should not be the same as the target control!
 * @param notReady - Input mode to set when submit is not ready. E.g. when input is invalid.
 * `on` (enabled) by default. An `off` (disable) value is a better choice for submit button.
 * @param invalid - Input mode to set when submit is not ready _and_ the form is submitted.
 * `on` (enabled) by default. An `off` (disable) value is a better choice for submit button.
 * @param busy - Input mode to set while submitting. `ro` (read-only) by default. An `off` (disabled) value is a better
 * choice for submit button.
 *
 * @returns A source of input mode.
 */
function inModeByForm(form, { notReady = 'on', invalid = 'on', busy = 'ro', } = {}) {
    const submit = form.aspect(InSubmit);
    return submit.read.do(mapAfter(flags => inModeValue(flags.busy ? busy : 'on', flags.ready ? 'on' : (flags.submitted ? invalid : notReady))));
}

/**
 * Creates an {@link InMode input mode} source depending on control's {@link InValidation validity}.
 *
 * This can be applied to form control to prevent submission of invalid input.
 *
 * @category Aspect
 * @param invalid - Input mode to set when the input is invalid. `-on` (not submittable) by default. This
 * should not be set to disabled, as the latter would make it impossible to fix input errors.
 * @param ignore - Ignored message codes. If all validation messages have this code the form is not marked as invalid.
 * `submit` by default, as this code intended to be server-side.
 *
 * @returns A source of input mode.
 */
function inModeByValidity({ invalid = '-on', ignore = 'submit', } = {}) {
    return control => control.aspect(InValidation).read.do(mapAfter(validity => validity.hasBut(...(typeof ignore === 'string' ? [ignore] : ignore)) ? invalid : 'on'));
}

/**
 * Abstract implementation of {@link InElement input HTML element control}.
 *
 * @category Control
 * @typeParam TValue - Input value type.
 * @typeParam TElt - A type of input HTML element.
 */
class AbstractInElement extends InElement {
    /**
     * Constructs HTML input element control.
     *
     * @param element - HTML input element the constructed control is based on.
     * @param aspects - Input aspects applied by default. These are aspect converters to constructed control
     * from the {@link inValueOf same-valued one}.
     * @param get - Input value getter.
     * @param set - Input value setter.
     */
    constructor(element, { aspects, get, set, }) {
        super({ aspects });
        this.element = element;
        this._input = new EventEmitter();
        const self = this;
        this._get = get;
        this._set = set;
        this._value = this.it;
        const doUpdate = this._update = (value, oldValue) => update({ value }, oldValue);
        this.events = new DomEventDispatcher(element);
        this.events.supply.needs(this);
        this.listenForInput(input => update(input, this._value));
        this.on = this._input.on.do(translateOn((send, { value: newValue }, oldValue) => newValue !== oldValue && send(newValue, oldValue)));
        this.input = this._input.on.do(mapAfter(asis, () => ({ value: this.it })));
        function update(input, oldValue) {
            for (;;) {
                self._value = input.value;
                // Corrections are value updates performed by update event receivers
                // The last correction is recorded and sent later, when all receivers receive current update
                let correction;
                // Record corrections
                self._update = (newValue, old) => {
                    // Corrections retain the event instance
                    correction = [{ ...input, value: newValue }, old];
                };
                try {
                    self._input.send(input, oldValue);
                }
                finally {
                    self._update = doUpdate;
                }
                if (!correction) {
                    break; // No more corrections
                }
                // Apply last correction
                // noinspection JSUnusedAssignment
                [input, oldValue] = correction;
            }
        }
    }
    get supply() {
        return this._input.supply;
    }
    get it() {
        return this._get();
    }
    set it(value) {
        const oldValue = this.it;
        if (value !== oldValue) {
            this._set(value);
            this._update(this._get(), oldValue);
        }
    }
    /**
     * Enables reaction to input input.
     *
     * By default listens for `input` and `change` events.
     *
     * @param update - Updates current value by user input and sends update event. This function is to be called by
     * input event listeners.
     */
    listenForInput(update) {
        const onInput = (event) => update({ value: this.it, event });
        this.events.on('input')(onInput);
        this.events.on('change')(onInput);
    }
}

function inCheckbox(element, { checked = true, unchecked = false, intermediate = undefined, aspects, } = {}) {
    return new AbstractInElement(element, {
        get() {
            return this.element.intermediate
                ? intermediate
                : this.element.checked ? checked : unchecked;
        },
        set(value) {
            this.element.checked = value === checked;
            this.element.intermediate = value !== checked && value !== unchecked;
        },
        aspects,
    });
}

/**
 * @internal
 */
class InRadioControl extends AbstractInElement {
    constructor(element, { checked = true, aspects, } = {}) {
        super(element, {
            aspects,
            get() {
                return this.element.checked ? checked : undefined;
            },
            set(value) {
                this.element.checked = value === checked;
            },
        });
    }
    _applyAspect(aspect) {
        if (aspect === InMode[InAspect__symbol]) {
            return applyRadioInMode(this);
        }
        return super._applyAspect(aspect);
    }
}
/**
 * @internal
 */
function applyRadioInMode(radio) {
    const { instance: mode } = InMode[InAspect__symbol].applyTo(radio);
    mode.derive(radio.read.do(mapAfter(value => value !== undefined ? 'on' : '-on')));
    return knownInAspect(mode);
}
function inRadio(element, values) {
    return new InRadioControl(element, values);
}

/**
 * @internal
 */
class InRadioGroupControl extends InControl {
    constructor(_buttons, { unchecked, aspects, } = {}) {
        super({ aspects });
        this._buttons = _buttons;
        this._unchecked = unchecked;
        const read = afterAll(_buttons).do(mapAfter(values => checkedInValue(values, unchecked)));
        this._it = trackValue(unchecked).by(read);
        this._it.on(value => {
            itsEach(overEntries(_buttons), ([key, button]) => {
                button.it = value === key || undefined;
            });
        });
    }
    get supply() {
        return this._it.supply;
    }
    get it() {
        return this._it.it;
    }
    set it(value) {
        this._it.it = value != null && this._buttons[value] ? value : this._unchecked;
    }
    get on() {
        return this._it.on;
    }
}
/**
 * @internal
 */
function checkedInValue(values, unchecked) {
    let checked = unchecked;
    itsEvery(overEntries(values), ([key, [value]]) => {
        if (value === undefined) {
            return true;
        }
        checked = key;
        return false;
    });
    return checked;
}
function inRadioGroup(buttons, values) {
    return new InRadioGroupControl(buttons, values);
}

/**
 * Creates input control for the given select element.
 *
 * The value of this control is an array of selected option values. This is particularly useful for `<select multiple>`
 * elements. Consider to use `inText()` for single-selects.
 *
 * @category Control
 * @param element - Target select element.
 * @param aspects - Input aspects applied by default. These are aspect converters to constructed control
 * from the {@link inValueOf same-valued one}.
 *
 * @return New select input control instance.
 */
function inSelect(element, { aspects, } = {}) {
    return new AbstractInElement(element, {
        aspects,
        get() {
            return itsElements(valueArray(this.element.options, option => option.selected && option.value));
        },
        set(value) {
            const selected = new Set(value);
            // Iterate in reverse order to ensure the first matching option is selected
            // when `multiple` attribute isn't set.
            itsEach(reverseArray(this.element.options), option => option.selected = selected.has(option.value));
        },
    });
}

/**
 * Creates control for the given textual input element.
 *
 * Note that this won't work for files, checkboxes, or radio buttons.
 *
 * For `<select multiple>` this would only reflect the first option. Consider to use `inSelect()` for multi-selects.
 *
 * @category Control
 * @param element - Target text input element.
 * @param aspects - Input aspects applied by default. These are aspect converters to constructed control
 * from the {@link inValueOf same-valued one}.
 *
 * @return New textual input control instance.
 */
function inText(element, { aspects, } = {}) {
    return new AbstractInElement(element, {
        get() {
            return this.element.value;
        },
        set(value) {
            this.element.value = value;
        },
        aspects,
    });
}

/**
 * @internal
 */
const InFocus__aspect = {
    applyTo(control) {
        return builtInAspect(control, InFocus, ctrl => {
            const element = ctrl.aspect(InElement);
            return element && new InControlFocus(element);
        });
    },
};
/**
 * Input focus aspect.
 *
 * This is a value tracker of element focus flag. Or `null` when {@link InElement} aspect is absent.
 *
 * @category Aspect
 */
class InFocus extends ValueTracker {
    static get [InAspect__symbol]() {
        return InFocus__aspect;
    }
}
/**
 * @internal
 */
class InControlFocus extends InFocus {
    constructor(inElement) {
        super();
        const { element, events } = inElement;
        const owner = element.getRootNode
            ? element.getRootNode()
            : element.ownerDocument;
        this._it = trackValue(owner.activeElement === element);
        this.supply.needs(inElement);
        events.on('focus')(() => this._it.it = true);
        events.on('blur')(() => this._it.it = false);
        this.on({
            receive(ctx, newValue) {
                ctx.onRecurrent(noop);
                if (newValue) {
                    element.focus();
                }
                else {
                    element.blur();
                }
            },
        });
    }
    get supply() {
        return this._it.supply;
    }
    get it() {
        return this._it.it;
    }
    set it(value) {
        this._it.it = value;
    }
    get on() {
        return this._it.on;
    }
}

/**
 * @internal
 */
const InStatus__aspect = {
    applyTo(control) {
        return builtInAspect(control, InStatus, ctrl => {
            const container = ctrl.aspect(InContainer);
            return container != null ? new InContainerStatus(container) : new InControlStatus(ctrl);
        });
    },
};
/**
 * Aggregate status aspect of user input.
 *
 * Collects and reports input status flags. Like whether the input ever had focus or being altered.
 *
 * Supports input elements and containers. For the rest of input controls always sends default status flags.
 *
 * Implements `EventKeeper` interface by sending collected status flags to receivers.
 *
 * @category Aspect
 */
class InStatus {
    static get [InAspect__symbol]() {
        return InStatus__aspect;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}
/**
 * @internal
 */
const defaultInStatusFlags = {
    hasFocus: false,
    touched: false,
    edited: false,
};
/**
 * @internal
 */
class InControlStatus extends InStatus {
    constructor(control) {
        super();
        this._flags = trackValue(defaultInStatusFlags);
        this._flags.supply.needs(control);
        this._flags.by(elementInStatusFlags(this._flags, control));
    }
    get read() {
        return this._flags.read;
    }
    markTouched(touched = true) {
        const flags = this._flags.it;
        if (!touched) {
            if (flags.touched) {
                // Try to reset touched.
                // Still touched if in focus. Not edited anyway.
                this._flags.it = { ...flags, touched: flags.hasFocus, edited: false };
            }
        }
        else if (!flags.touched) {
            // Do not modify if already touched.
            this._flags.it = { ...flags, touched };
        }
        return this;
    }
    markEdited(edited = true) {
        const flags = this._flags.it;
        if (edited) {
            if (!flags.edited) {
                // Touched if edited
                this._flags.it = { ...flags, touched: true, edited };
            }
        }
        else if (flags.edited) {
            // Assume not edited
            this._flags.it = { ...flags, edited };
        }
        return this;
    }
}
/**
 * @internal
 */
function elementInStatusFlags(origin, control) {
    const element = control.aspect(InElement);
    const focus = control.aspect(InFocus);
    return afterAll({
        hasFocus: focus || afterThe(false),
        edited: element ? element.input.do(mapAfter(({ event }) => !!event)) : afterThe(false),
    }).do(mapAfter(({ hasFocus: [hasFocus], edited: [edited] }) => updateInStatusFlags(origin.it, hasFocus, edited)));
}
/**
 * @internal
 */
function updateInStatusFlags(flags, hasFocus, edited) {
    if (hasFocus) {
        flags = { ...flags, hasFocus, touched: true };
    }
    else {
        flags = { ...flags, hasFocus };
    }
    if (edited) {
        flags = { ...flags, edited, touched: true };
    }
    return flags;
}
/**
 * @internal
 */
class InContainerStatus extends InStatus {
    constructor(_container) {
        super();
        this._container = _container;
        this.read = containerInStatusFlags(this._container);
    }
    markEdited(edited) {
        this._container.controls.read.do(onceAfter)(snapshot => itsEach(snapshot, control => control.aspect(InStatus).markEdited(edited)));
        return this;
    }
    markTouched(touched) {
        this._container.controls.read.do(onceAfter)(snapshot => itsEach(snapshot, control => control.aspect(InStatus).markTouched(touched)));
        return this;
    }
}
/**
 * @internal
 */
function containerInStatusFlags(container) {
    return container.controls.read.do(supplyAfter(container), digAfter_((snapshot) => afterEach(...inControlStatuses(snapshot))), mapAfter(combineInStatusFlags));
}
/**
 * @internal
 */
function inControlStatuses(snapshot) {
    return mapIt(snapshot, c => c.aspect(InStatus));
}
/**
 * @internal
 */
function combineInStatusFlags(...flags) {
    const result = {
        hasFocus: false,
        touched: false,
        edited: false,
    };
    itsEach(flags, (([{ hasFocus, touched, edited }]) => {
        if (touched) {
            result.touched = true;
        }
        if (hasFocus) {
            result.hasFocus = result.touched = true;
        }
        if (edited) {
            result.edited = result.touched = true;
        }
    }));
    return result;
}

/**
 * Creates form element control.
 *
 * @category Control
 * @param element - HTML element to create control for.
 * @param options - Form element control options.
 *
 * @returns New form element control.
 */
function inFormElement(element, options) {
    const { form, aspects, modes } = options;
    const control = new AbstractInElement(element, {
        aspects: [intoConvertedAspects(aspects)],
        get: noop,
        set: noop,
    });
    control.supply.needs(form);
    return control.setup(InMode, mode => mode.derive(inModeByForm(form, modes)));
}

/**
 * Creates submit button control.
 *
 * @category Control
 * @param element - Submit button element to create control for.
 * @param options - Submit button control options.
 *
 * @returns New submit button control.
 */
function inSubmitButton(element, options) {
    const { form, aspects, modes: { notReady = 'on', invalid = 'off', busy = 'off' } = {} } = options;
    const control = new AbstractInElement(element, {
        aspects: [intoConvertedAspects(aspects)],
        get: noop,
        set: noop,
    });
    control.supply.needs(form);
    control.setup(InMode, mode => mode.derive(inModeByForm(form, { notReady, invalid, busy })));
    return control;
}

/**
 * @internal
 */
const InStyledElement__aspect = {
    applyTo(control) {
        const element = control.aspect(InElement);
        return element ? knownInAspect(element.element) : nullInAspect();
    },
};
/**
 * @category Aspect
 */
const InStyledElement = {
    get [InAspect__symbol]() {
        return InStyledElement__aspect;
    },
    /**
     * Creates input control aspect converter that assigns the given styled element to converted control.
     *
     * This is useful for controls without elements (such as input groups), or can be used to apply CSS classes to input
     * element wrappers (such as `form-group` in Bootstrap).
     *
     * @typeParam TValue - Converted control value type.
     * @param element - A DOM element to apply styles to. Styles won't be applied when `null` or undefined.
     *
     * @returns Input control aspect converter.
     */
    to(element = null) {
        return {
            applyAspect(aspect) {
                return aspect === InStyledElement__aspect
                    ? knownInAspect(element)
                    : undefined;
            },
        };
    },
};

/**
 * @internal
 */
const InCssClasses__aspect = {
    applyTo(control) {
        return {
            instance: new InControlCssClasses(control),
            convertTo: noop,
        };
    },
};
/**
 * An aspect of the user input representing CSS classes to apply to styled element.
 *
 * Implements an `EventKeeper` interface by reporting all CSS classes applied to styled element.
 *
 * @category Aspect
 */
class InCssClasses {
    static get [InAspect__symbol]() {
        return InCssClasses__aspect;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
}
/**
 * @internal
 */
const UnsubscribeReason__symbol = ( /*#__PURE__*/Symbol('reason'));
/**
 * @internal
 */
function isUnsubscribeReason(reason) {
    return reason && typeof reason === 'object' && UnsubscribeReason__symbol in reason;
}
/**
 * @internal
 */
class InControlCssClasses extends InCssClasses {
    constructor(_control) {
        super();
        this._control = _control;
        this._sources = trackValue([
            new Map(),
        ]);
        _control.supply.whenOff(reason => this.done(reason));
        this.read = this._sources.read.do(supplyAfter(this._control), digAfter_(([sources]) => afterEach(...sources.keys())), mapAfter_((...classes) => {
            const result = {};
            classes.forEach(([map]) => mergeInCssClassesMap(map, result));
            return result;
        }));
        this.track = afterEventBy(receiver => {
            receiver.supply.needs(this._control);
            const classes = new DeltaSet();
            const emitter = new EventNotifier();
            let classesSent = false;
            const sendClasses = () => {
                classesSent = true;
                classes.redelta((add, remove) => emitter.send(add, remove)).undelta();
            };
            emitter.on(receiver);
            return this.read(map => {
                const remove = new Set(classes);
                const add = [];
                itsEach(filterIt(overEntries(map), ([, flag]) => !!flag), ([name]) => {
                    if (!remove.delete(name)) {
                        add.push(name);
                    }
                });
                if (!classesSent || add.length || remove.size) {
                    classes.delta(add, remove);
                    sendClasses();
                }
            });
        });
        const element = _control.aspect(InStyledElement);
        if (element) {
            this.applyTo(element, this.schedule);
        }
    }
    get schedule() {
        return this._schedule || (this._schedule = controlSchedule(this._control, this._control.aspect(InStyledElement)));
    }
    specs(source) {
        return afterSupplied(isEventKeeper(source) ? source : source(this._control));
    }
    resolve(source) {
        const nsAlias = this._control.aspect(InNamespaceAliaser);
        return this.specs(source).do(mapAfter((...names) => {
            const result = {};
            names.forEach(name => {
                if (isQualifiedName(name)) {
                    result[css__naming.name(name, nsAlias)] = true;
                }
                else {
                    mergeInCssClassesMap(name, result);
                }
            });
            return result;
        }));
    }
    add(source) {
        const inSupply = this._control.supply;
        if (inSupply.isOff) {
            return inSupply;
        }
        const classesSupply = new Supply();
        const src = afterEventBy(receiver => {
            const supply = this.resolve(source)({
                receive(context, ...event) {
                    receiver.receive(context, ...event);
                },
            });
            receiver.supply.whenOff(reason => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                classesSupply.off({ [UnsubscribeReason__symbol]: reason });
            });
            classesSupply.needs(supply).whenOff(reason => {
                if (isUnsubscribeReason(reason)) {
                    supply.off(reason[UnsubscribeReason__symbol]);
                }
            });
        }).do(shareAfter);
        const [sources] = this._sources.it;
        sources.set(src, classesSupply);
        classesSupply.whenOff(reason => {
            if (!isUnsubscribeReason(reason)) {
                sources.delete(src);
                this._sources.it = [sources];
            }
        });
        this._sources.it = [sources];
        return classesSupply.needs(inSupply);
    }
    applyTo(element, schedule = controlSchedule(this._control, element)) {
        const { classList } = element;
        const classes = new DeltaSet();
        const updateClasses = () => {
            classes.redelta((add, remove) => {
                classList.remove(...remove);
                classList.add(...add);
            }).undelta();
        };
        return this.track((add, remove) => {
            classes.delta(add, remove);
            schedule(updateClasses);
        }).whenOff(() => {
            if (classes.size) {
                classes.clear();
                schedule(updateClasses);
            }
        });
    }
    done(reason) {
        itsEach(this._sources.it[0].values(), supply => supply.off(reason));
        this._sources.supply.off(reason);
        return this;
    }
}
/**
 * @internal
 */
function mergeInCssClassesMap(map, result) {
    itsEach(overEntries(map), ([name, flag]) => {
        if (flag != null) {
            result[name] = flag;
        }
    });
}
/**
 * @internal
 */
function controlSchedule(control, node) {
    return control.aspect(InRenderScheduler)({ node });
}

/**
 * Builds a source of error marker CSS classes.
 *
 * Applies error marker class(es) when the given error message codes present in
 * {@link InValidation.Result validation result}.
 *
 * @category Style
 * @param mark - Error mark. Specifies CSS class(es) to apply when requested error present.
 * A class with `has-error` name in {@link InputAspects__NS input aspects namespace} is used by default.
 * @param when - {@link InValidation.Message Validation message} code(s) to expect.
 * {@link InValidation.Result.ok Any} error matches by default.
 *
 * @returns A source of CSS class names to apply.
 */
function inCssError({ mark, when, } = {}) {
    let hasError;
    if (!when) {
        hasError = defaultInCssHasError;
    }
    else if (isArrayOfElements(when)) {
        hasError = when.length ? errors => when.every(code => errors.has(code)) : defaultInCssHasError;
    }
    else {
        hasError = errors => errors.has(when);
    }
    return control => control.aspect(InValidation).read.do(translateAfter((send, errors) => hasError(errors) ? send(...inCssErrorMarks(mark)) : send()));
}
/**
 * @internal
 */
function defaultInCssHasError(errors) {
    return !errors.ok;
}
/**
 * @internal
 */
const defaultInCssErrorMarks = [['has-error', InputAspects__NS]];
/**
 * @internal
 */
function inCssErrorMarks(mark) {
    if (!mark) {
        return defaultInCssErrorMarks;
    }
    const marks = arrayOfElements(mark);
    return marks.length ? marks : defaultInCssErrorMarks;
}

/**
 * Builds a source of informative CSS classes.
 *
 * Generates the following CSS classes:
 * - `disabled` when input control is disabled (i.e. has no data).
 * - `readonly` when input control is read-only,
 * - `invalid` when input control validation failed,
 * - `missing` when there are validation messages with `missing` code,
 * - `incomplete` when there are validation messages with `incomplete`,
 * - `has-focus` when input control has input focus,
 * - `touched` when input control is touched (i.e. had focus already),
 * - `edited` when input control is edited by user.
 *
 * These names are qualified with the given (or {@link InputAspects__NS default}) namespace.
 *
 * @category Style
 * @param ns - A definition of namespace to qualify CSS class names with. The {@link InputAspects__NS default namespace}
 * will be used when omitted.
 *
 * @returns A source of CSS class names to apply.
 */
function inCssInfo({ ns = InputAspects__NS, } = {}) {
    return (control) => {
        const cls = (name) => [name, ns];
        return afterAll({
            md: control.aspect(InMode),
            vl: control.aspect(InValidation),
            st: control.aspect(InStatus),
        }).do(translateAfter((send, { md: [mode], vl: [valid], st: [{ hasFocus, touched, edited }] }) => {
            const names = [];
            if (!InMode.hasData(mode)) {
                names.push(cls('disabled'));
            }
            if (mode === 'ro' || mode === '-ro') {
                names.push(cls('readonly'));
            }
            if (!valid.ok) {
                names.push(cls('invalid'));
            }
            if (valid.has('missing')) {
                names.push(cls('missing'));
            }
            if (valid.has('incomplete')) {
                names.push(cls('incomplete'));
            }
            if (hasFocus) {
                names.push(cls('has-focus'));
            }
            if (touched) {
                names.push(cls('touched'));
            }
            if (edited) {
                names.push(cls('edited'));
            }
            send(...names);
        }));
    };
}

export { AbstractInElement, InAspect__symbol, InBuilder, InContainer, InContainerControls, InControl, InCssClasses, InData, InElement, InFocus, InGroup, InGroupControls, InList, InListControls, InMode, InNamespaceAliaser, InParents, InParser, InRenderScheduler, InRole, InStatus, InStyledElement, InSubmit, InSubmitError, InSubmitRejectedError, InValidation, InputAspects__NS, builtInAspect, inCheckbox, inCssError, inCssInfo, inFormElement, inGroup, inList, inModeByForm, inModeByValidity, inModeValue, inRadio, inRadioGroup, inSelect, inSubmitButton, inText, inValidationResult, inValidator, inValue, inValueOf, inconvertibleInAspect, intoConvertedAspects, intoConvertedBy, intoFallback, intoInteger, intoParsedBy, intoTrimmed, isInAspectConversion, knownInAspect, nullInAspect, requireAll, requireLength, requireNeeded, requireNothing, requirePresent, requireRange };
//# sourceMappingURL=input-aspects.js.map
